export class Node<T> {
	weight: number;
	data: T;

	left: Node<T> | null = null;
	right: Node<T> | null = null;
	height = 1;

	constructor(weight: number, data: T) {
		this.weight = weight;
		this.data = data;
	}

	get leftHeight() {
		return this.left?.height ?? 0;
	}

	get rightHeight() {
		return this.right?.height ?? 0;
	}

	get balanceFactor() {
		return this.leftHeight - this.rightHeight;
	}

	/**
	 *     b                           a
	 *    / \                         / \
	 *   a   e -> b.rotateRight() -> c   b
	 *  / \                             / \
	 * c   d                           d   e
	 */
	rotateRight() {
		const other = this.left!;
		this.left = other.right;
		other.right = this;

		this.updateHeight();
		other.updateHeight();

		return other;
	}

	/**
	 *   a                           b
	 *  / \                         / \
	 * c   b -> a.rotateLeft() -> a   e
	 *    / \                    / \
	 *   d   e                  c   d
	 */
	rotateLeft() {
		const other = this.right!;
		this.right = other.left;
		other.left = this;

		this.updateHeight();
		other.updateHeight();

		return other;
	}

	updateHeight() {
		this.height = Math.max(this.leftHeight, this.rightHeight) + 1;
	}
}

/**
 * @returns -1 if should go to the left, 1 if should go to the right and 0 if found
 */
export type SearchFn<T> = (node: Node<T>) => number;

export class AvlTree<T> {
	root: Node<T> | null;

	size = 0;

	constructor() {
		this.root = null;
	}

	insert(node: Node<T>) {
		this.root = this.#insertNode(this.root, node);
		this.size++;
	}

	search(searchFn: SearchFn<T>): T | null {
		if (this.root == null) return null;
		return this.#search(this.root, searchFn);
	}

	#search(root: Node<T> | null, searchFn: SearchFn<T>): T | null {
		if (root == null) return null;

		const compare = searchFn(root);

		if (compare === 0) {
			return root.data;
		}

		if (compare < 0) {
			return this.#search(root?.left ?? null, searchFn);
		} else {
			return this.#search(root?.right ?? null, searchFn);
		}
	}

	#insertNode(root: Node<T> | null, node: Node<T>): Node<T> | null {
		if (root == null) return node;

		if (node.weight < root.weight) {
			root.left = this.#insertNode(root.left, node);
		} else if (node.weight > root.weight) {
			root.right = this.#insertNode(root.right, node);
		} else {
			// Duplicate data, nothing to do.
			return root;
		}

		root.updateHeight();

		const { balanceFactor } = root;

		// Need to balance left
		if (balanceFactor > 1) {
			if (node.weight < root.left!.weight) {
				root = root.rotateRight();
			} else if (node.weight > root.left!.weight) {
				root.left = root.left!.rotateLeft();
				return root.rotateRight();
			}
		}

		// Need to balance right
		if (balanceFactor < -1) {
			if (node.weight > root.right!.weight) {
				return root.rotateLeft();
			} else if (node.weight < root.right!.weight) {
				root.right = root.right!.rotateRight();
				return root.rotateLeft();
			}
		}

		return root;
	}

	*[Symbol.iterator]() {
		yield* this.#read(this.root);
	}

	*entries(): Generator<[number, T]> {
		let i = 0;
		for (const data of this) {
			yield [i, data];
			++i;
		}
	}

	*#read(node: Node<T> | null): Generator<T> {
		if (node) {
			yield* this.#read(node.left);
			yield node.data;
			yield* this.#read(node.right);
		}
	}
}
